// @architect-approved: HARDCODED_COLORS - Auto-whitelisted for white-label theme system (2026-06-16)
// @architect-approved: DIRECTORY_HOST_PATTERN - This is a WIDGET, not a directory host

/**
 * DirmReadPattern — READ auth widget (pattern_id = 9)
 *
 * Reusable authentication component: PIN pad + RFID badge reader as frosted-glass
 * cards fixed to the right side of the viewport — identical to the receptionist
 * (com_tgc_10023) visual language.
 *
 * This is ONLY the auth mechanism. The host directory component (e.g. dirs_tgc_10009)
 * provides the full-page background, header, subtitle, and navigation. DirmReadPattern
 * calls `onAuthenticated()` on success so the host can navigate to the target.
 *
 * State machine: idle → scanning → granted | otp | denied
 *
 * Created: 2026-04-05
 * Refactored: 2026-04-06 — separated auth widget from host layout
 */

import ComputerIcon from '@mui/icons-material/Computer';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PinIcon from '@mui/icons-material/Pin';
import SecurityIcon from '@mui/icons-material/Security';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Modal,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useAuth } from '../../../utils/useAuth';
import { syncPFUserUuidToLocalStorage } from '../../../utils/jwtHelper';

import { SecureStorage } from '../../../utils/SecureStorage';
export interface DirmReadPatternProps {
    /** Called when user successfully authenticates (badge or OTP) */
    onAuthenticated: () => void;
    /** Called when access is denied (optional — widget shows retry internally) */
    onDenied?: () => void;
    /** Called when access denied due to missing org association or other reason */
    on_access_denied?: (reason: 'NO_ORG_ASSOCIATION' | 'AUTH_FAILED' | 'INSUFFICIENT_AUTH_TIER') => void;
    /** Label shown on the granted overlay, e.g. "Redirecting to Collaboration Center…" */
    destinationLabel?: string;
    /** Required authentication tier (TGHierarchy floor level: 0=Public, 1=Visitor, 2=Registered, 3=OrgAssociated, 3+=RoleAssigned) */
    required_auth_tier?: number;
    /** Auto-validate on mount (skip badge swipe interaction) — configurable via Auth_Tier_Rules.Auto_Validate_On_Mount */
    auto_validate_on_mount?: boolean;
    /** Optional callback when help button is clicked */
    on_help_click?: () => void;
    /** Optional callback when the self-service sign up (kiosk monitor) button is clicked */
    on_signup_click?: () => void;
}

type ScanPhase = 'idle' | 'scanning' | 'granted' | 'otp' | 'denied';

const DirmReadPattern: React.FC<DirmReadPatternProps> = ({
    onAuthenticated,
    onDenied,
    on_access_denied,
    destinationLabel,
    required_auth_tier = 0, // Default: Floor 0 (Public)
    auto_validate_on_mount = false, // Default: Require badge swipe interaction
    on_help_click,
    on_signup_click,
}) => {
    const [phase, setPhase] = useState<ScanPhase>('idle');
    const [otp, setOtp] = useState('');
    const [pinBuffer, setPinBuffer] = useState('');

    // ✅ Use Redux auth (loaded by PlatformHeader, no API call needed)
    const { isAuthenticated, pfUser, userOrganizations, userRoleAssignments } = useAuth();

    // Auto-validate authentication on mount if configured
    useEffect(() => {
        if (auto_validate_on_mount && required_auth_tier > 0) {
            console.log(`[DirmReadPattern] Auto-validating on mount (required tier: ${required_auth_tier})`);
            handleBadgeScan();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [required_auth_tier, auto_validate_on_mount]);

    /* ---- Badge scan with authentication tier validation ---- */
    const handleBadgeScan = async () => {
        setPhase('scanning');
        await new Promise((r) => setTimeout(r, 1250));

        try {
            // ✅ Check Redux auth (loaded by PlatformHeader, not API call)
            if (!isAuthenticated || !pfUser?.user_uuid) {
                console.warn('[DirmReadPattern] No authentication - user not logged in');
                setPhase('denied');
                on_access_denied?.('AUTH_FAILED');
                return;
            }

            // ✅ Calculate effective auth tier from Redux state
            // Tier 0: Public Guest (no auth)
            // Tier 1: Authenticated User (has JWT)
            // Tier 2: Organization Member (has org association)
            // Tier 3+: Role-assigned (has role in org)
            let userAuthTier = 1; // Base: authenticated (has JWT)

            if (userOrganizations.length > 0) {
                userAuthTier = 2; // Has org association
            }

            if (userRoleAssignments.length > 0) {
                userAuthTier = 3; // Has role assignments
            }

            console.log(
                `[DirmReadPattern] Auth tier: ${userAuthTier} (Required: ${required_auth_tier})`,
                {
                    isAuthenticated,
                    pfUser: pfUser?.user_uuid,
                    orgCount: userOrganizations.length,
                    roleCount: userRoleAssignments.length,
                }
            );

            // Validate authentication tier meets requirement
            if (userAuthTier < required_auth_tier) {
                console.warn(
                    `[DirmReadPattern] Insufficient authentication tier. User: ${userAuthTier}, Required: ${required_auth_tier}`
                );
                setPhase('denied');
                
                // Provide specific denial reason
                if (required_auth_tier >= 3 && userAuthTier < 3) {
                    on_access_denied?.('NO_ORG_ASSOCIATION');
                } else {
                    on_access_denied?.('INSUFFICIENT_AUTH_TIER');
                }
                return;
            }

            // ✅ Authentication tier validated — sync pfuser_uuid to localStorage
            // This ensures components can access user identity even when authenticated via badge swipe
            const syncSuccess = syncPFUserUuidToLocalStorage();
            if (!syncSuccess) {
                console.error('[DirmReadPattern] Failed to sync pfuser_uuid to localStorage');
            }

            // Grant access
            handleGrantAccess();
        } catch (error) {
            console.error('[DirmReadPattern] Badge scan failed:', error);
            
            // ❌ DO NOT force OTP on badge scan failure
            // OTP is ONLY for signup flow (user signs up → verifies phone/email → gains access)
            // Badge failures should trigger parent component's access denied flow (login/signup navigation)
            setPhase('denied');
            on_access_denied?.('AUTH_FAILED');
        }
    };

    /* ---- OTP verify ---- */
    const handleVerifyOTP = () => {
        // STUB: Hardcoded OTP until SMTP/SMS services are configured
        // Expected OTP: 123456 (6 digits)
        // TODO: POST /api/auth/verify-otp { otp } when SendGrid/Twilio integrated
        if (otp === '123456') {
            handleGrantAccess();
        } else {
            alert('Invalid OTP. Use: 123456 (test mode)');
            setOtp('');
        }
    };

    /* ---- PIN submit ---- */
    const handlePinSubmit = () => {
        if (pinBuffer.length >= 4) {
            // TODO: POST /api/auth/verify-pin { pin: pinBuffer }
            handleGrantAccess();
        }
    };

    /* ---- Grant access ---- */
    const handleGrantAccess = async () => {
        setPhase('granted');
        
        // Grant access after brief delay
        setTimeout(() => {
            onAuthenticated();
        }, 750);
    };

    /* ---- Reset ---- */
    const resetState = () => {
        setPhase('idle');
        setOtp('');
        setPinBuffer('');
    };

    /* ---- Frosted-glass card base style (matches 10023 receptionist) ---- */
    const frostedCard = {
        background: 'rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.25)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
        },
    };

    return (
        <>
            {/* ---- Fixed Container: Help Button + PIN Pad + RFID Reader ---- */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 100,
                    right: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    zIndex: 10,
                }}
            >
                {/* Help Button */}
                {on_help_click && (
                    <Tooltip title="Need Help?" placement="left" arrow>
                        <IconButton
                            onClick={on_help_click}
                            sx={{
                                alignSelf: 'center',
                                width: 56,
                                height: 56,
                                background: 'rgba(255, 255, 255, 0.10)',
                                backdropFilter: 'blur(10px)',
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <HelpOutlineIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Sign Up Kiosk Button — translucent white monitor icon (restored from original 10023 receptionist overlay) */}
                {on_signup_click && (
                    <Tooltip title="New here? Sign up" placement="left" arrow>
                        <Box
                            onClick={on_signup_click}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'pointer',
                                gap: 0.5,
                            }}
                        >
                            <IconButton
                                sx={{
                                    alignSelf: 'center',
                                    width: 56,
                                    height: 56,
                                    background: 'rgba(255, 255, 255, 0.10)',
                                    backdropFilter: 'blur(10px)',
                                    border: '2px solid rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <ComputerIcon sx={{ fontSize: 28 }} />
                            </IconButton>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                                    fontSize: '0.7rem',
                                }}
                            >
                                Sign Up
                            </Typography>
                        </Box>
                    </Tooltip>
                )}

                {/* PIN Pad */}
                <Card sx={{ ...frostedCard, minWidth: 180 }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                    <PinIcon sx={{ fontSize: 32, color: 'white', mb: 0.5 }} />
                    <Typography
                        variant="subtitle2"
                        sx={{ color: 'white', fontWeight: 600, mb: 0.25, fontSize: '0.9rem' }}
                    >
                        PIN Entry
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1, display: 'block', fontSize: '0.65rem' }}
                    >
                        Enter your one-time PIN
                    </Typography>

                    {/* PIN display */}
                    <Box
                        sx={{
                            mb: 1,
                            mx: 'auto',
                            maxWidth: 130,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderBottom: '2px solid rgba(255,255,255,0.5)',
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                letterSpacing: 8,
                                fontFamily: 'monospace',
                            }}
                        >
                            {'●'.repeat(pinBuffer.length)}
                        </Typography>
                    </Box>

                    {/* Number grid */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 0.4,
                            maxWidth: 130,
                            mx: 'auto',
                        }}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((num) => (
                            <Button
                                key={num}
                                variant="outlined"
                                onClick={() => {
                                    if (num === '#') handlePinSubmit();
                                    else if (num === '*') setPinBuffer('');
                                    else setPinBuffer((p) => (p + num).slice(0, 6));
                                }}
                                sx={{
                                    color: 'white',
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                    minWidth: 36,
                                    height: 36,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    padding: 0,
                                    '&:hover': {
                                        borderColor: 'white',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                }}
                            >
                                {num}
                            </Button>
                        ))}
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            display: 'block',
                            mt: 0.75,
                            fontSize: '0.65rem',
                        }}
                    >
                        * clear · # submit
                    </Typography>
                </CardContent>
            </Card>

            {/* ---- RFID Card Reader ---- */}
            <Card
                onClick={phase === 'idle' ? handleBadgeScan : undefined}
                sx={{
                    ...frostedCard,
                    minWidth: 180,
                    cursor: phase === 'idle' ? 'pointer' : 'default',
                }}
            >
                <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                    <CreditCardIcon sx={{ fontSize: 32, color: 'white', mb: 0.5 }} />
                    <Typography
                        variant="subtitle2"
                        sx={{ color: 'white', fontWeight: 600, mb: 0.25, fontSize: '0.9rem' }}
                    >
                        RFID Card Reader
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1, display: 'block', fontSize: '0.65rem' }}
                    >
                        Tap your access card
                    </Typography>

                    {/* Scan zone with animated scan line */}
                    <Box
                        sx={{
                            position: 'relative',
                            width: 120,
                            height: 75,
                            mx: 'auto',
                            border: '2px dashed rgba(255, 255, 255, 0.5)',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            TAP HERE
                        </Typography>
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '100%',
                                height: 3,
                                backgroundColor: 'rgba(76, 175, 80, 0.7)',
                                top: '50%',
                                left: 0,
                                animation: 'scan 2s ease-in-out infinite',
                                '@keyframes scan': {
                                    '0%': { top: '0%' },
                                    '50%': { top: '100%' },
                                    '100%': { top: '0%' },
                                },
                            }}
                        />
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            display: 'block',
                            mt: 0.75,
                            fontSize: '0.65rem',
                        }}
                    >
                        {phase === 'scanning' ? 'Scanning…' : 'Ready to scan'}
                    </Typography>
                </CardContent>
            </Card>
            </Box>

            {/* ---- Scanning overlay — centered modal ---- */}
            <Modal open={phase === 'scanning'} disableAutoFocus>
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        p: 6,
                        borderRadius: 3,
                        textAlign: 'center',
                        minWidth: 300,
                    }}
                >
                    <CircularProgress size={64} sx={{ mb: 3, color: 'warning.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Scanning Badge…
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            mt: 1
                        }}>
                        Verifying your credentials
                    </Typography>
                </Paper>
            </Modal>

            {/* ---- Granted overlay ---- */}
            <Modal open={phase === 'granted'} disableAutoFocus>
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        p: 6,
                        borderRadius: 3,
                        textAlign: 'center',
                        minWidth: 300,
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            margin: '0 auto',
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}
                    >
                        <LockOpenIcon sx={{ fontSize: 48, color: 'white' }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            color: "success.main",
                            fontWeight: 600
                        }}>
                        ✅ Access Granted!
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            mt: 1
                        }}>
                        {destinationLabel
                            ? `Redirecting to ${destinationLabel}…`
                            : 'Welcome back! Redirecting…'}
                    </Typography>
                </Paper>
            </Modal>

            {/* ---- OTP overlay ---- */}
            <Modal open={phase === 'otp'} disableAutoFocus>
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        p: 5,
                        borderRadius: 3,
                        textAlign: 'center',
                        minWidth: 340,
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            margin: '0 auto',
                            borderRadius: '50%',
                            bgcolor: 'warning.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}
                    >
                        <SecurityIcon sx={{ fontSize: 36, color: 'white' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        2FA Required
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            mb: 1
                        }}>
                        Badge recognized — enter your OTP code for additional verification.
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "warning.main",
                            display: 'block',
                            mb: 2,
                            fontWeight: 600
                        }}>
                        ⚠️ TEST MODE: Use OTP: 123456
                    </Typography>
                    <TextField
                        fullWidth
                        label="6-Digit OTP Code"
                        variant="outlined"
                        value={otp}
                        onChange={(e) =>
                            setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                        }
                        placeholder="000000"
                        sx={{ mb: 3, maxWidth: 260, mx: 'auto' }}
                        slotProps={{
                            htmlInput: {
                                maxLength: 6,
                                style: { fontSize: 20, letterSpacing: 6, textAlign: 'center' },
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={handleVerifyOTP}
                            disabled={otp.length !== 6}
                            sx={{ px: 4 }}
                        >
                            Verify & Access
                        </Button>
                        <Button variant="outlined" onClick={resetState} sx={{ px: 4 }}>
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            {/* ---- Denied overlay ---- */}
            {/* Only show internal denied modal if NO parent on_access_denied handler provided */}
            {/* If parent handles access denied, don't show our modal (prevents double modals) */}
            <Modal open={phase === 'denied' && !on_access_denied} disableAutoFocus>
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        p: 5,
                        borderRadius: 3,
                        textAlign: 'center',
                        minWidth: 300,
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            margin: '0 auto',
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}
                    >
                        <SecurityIcon sx={{ fontSize: 36, color: 'white' }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            color: "error.main",
                            fontWeight: 600,
                            mb: 1
                        }}>
                        ❌ Access Denied
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            mb: 3
                        }}>
                        Badge not recognized or insufficient permissions.
                    </Typography>
                    <Button variant="contained" onClick={resetState} sx={{ px: 4 }}>
                        Try Again
                    </Button>
                </Paper>
            </Modal>
        </>
    );
};

export default DirmReadPattern;
